// src/config/database/lfMaterialsConfig.js

export const lfMaterialsTableConfig = {
  // Table identification
  id: 'lf_materials',
  name: 'Large Format Materials',
  apiEndpoint: '/api/admin/large-format/materials',
  
  // Column definitions for the DataTable
  columns: [
    {
      key: 'name',
      label: 'Material Name',
      sortable: true,
      width: '20%',
      render: (value, record) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{value}</span>
          <span className="text-xs text-gray-500">{record.product_no || 'No product #'}</span>
        </div>
      )
    },
    {
      key: 'material_type',
      label: 'Type',
      sortable: true,
      width: '10%',
      render: (value) => {
        const typeStyles = {
          'vinyl': 'bg-blue-100 text-blue-800',
          'board': 'bg-green-100 text-green-800',
          'banner': 'bg-yellow-100 text-yellow-800',
          'poster': 'bg-purple-100 text-purple-800',
          'laminate': 'bg-red-100 text-red-800'
        };
        
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${typeStyles[value] || 'bg-gray-100 text-gray-800'}`}>
            {value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Unknown'}
          </span>
        );
      }
    },
    {
      key: 'dimensions',
      label: 'Dimensions',
      sortable: false,
      width: '15%',
      render: (value, record) => {
        const width = record.width_mm ? `${record.width_mm}mm` : '-';
        const height = record.height_mm ? `${record.height_mm}mm` : '-';
        const length = record.roll_length_m ? `${record.roll_length_m}m` : '';
        
        if (record.material_type === 'board') {
          return (
            <div className="text-sm text-gray-700">
              <div>{width} × {height}</div>
              {record.area_sqm && <div className="text-xs text-gray-500">{record.area_sqm}m²</div>}
            </div>
          );
        } else {
          return (
            <div className="text-sm text-gray-700">
              <div>W: {width}</div>
              {length && <div className="text-xs text-gray-500">L: {length}</div>}
            </div>
          );
        }
      }
    },
    {
      key: 'pricing',
      label: 'Pricing',
      sortable: false,
      width: '20%',
      render: (value, record) => {
        const formatPrice = (price) => price ? `£${parseFloat(price).toFixed(2)}` : '-';
        const formatMarkup = (markup) => markup ? `${parseFloat(markup).toFixed(0)}%` : '0%';
        
        return (
          <div className="text-sm">
            {record.unit_type === 'sqm' && record.price_per_sqm && (
              <div className="text-blue-600 font-medium">{formatPrice(record.price_per_sqm)}/m²</div>
            )}
            {record.unit_type === 'linear_m' && record.price_per_linear_m && (
              <div className="text-green-600 font-medium">{formatPrice(record.price_per_linear_m)}/m</div>
            )}
            {record.price && (
              <div className="text-gray-600 text-xs">Base: {formatPrice(record.price)}</div>
            )}
            <div className="text-gray-500 text-xs">
              <div>Trade: +{formatMarkup(record.markup_percent)}</div>
              <div>Retail: +{formatMarkup(record.retail_markup_percent)}</div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'unit_type',
      label: 'Unit Type',
      sortable: true,
      width: '10%',
      render: (value) => {
        const unitStyles = {
          'sqm': 'bg-blue-100 text-blue-800',
          'linear_m': 'bg-green-100 text-green-800',
          'item': 'bg-gray-100 text-gray-800'
        };
        
        const unitLabels = {
          'sqm': 'per m²',
          'linear_m': 'per m',
          'item': 'per item'
        };
        
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-md ${unitStyles[value] || 'bg-gray-100 text-gray-800'}`}>
            {unitLabels[value] || value}
          </span>
        );
      }
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: '12%',
      render: (value) => (
        <span className="text-gray-700 text-sm">
          {value || '-'}
        </span>
      )
    },
    {
      key: 'detail',
      label: 'Details',
      sortable: true,
      width: '13%',
      render: (value) => (
        <span className="text-gray-600 text-sm truncate max-w-xs" title={value}>
          {value || '-'}
        </span>
      )
    }
  ],

  // Field definitions for the RecordModal form
  fields: [
    {
      key: 'material_type',
      label: 'Material Type',
      type: 'select',
      required: true,
      options: [
        { value: 'vinyl', label: '🔵 Vinyl' },
        { value: 'board', label: '🟢 Board' },
        { value: 'banner', label: '🟡 Banner' },
        { value: 'poster', label: '🟣 Poster' },
        { value: 'laminate', label: '🔴 Laminate' }
      ],
      help: 'The type of material determines which fields are required'
    },
    {
      key: 'name',
      label: 'Material Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., Premium Vinyl White, 3mm Foam Board',
      help: 'A descriptive name for this material'
    },
    {
      key: 'product_no',
      label: 'Product Number',
      type: 'text',
      required: false,
      placeholder: 'e.g., VIN-001, BRD-3MM-WHT',
      help: 'Optional product number or SKU'
    },
    {
      key: 'detail',
      label: 'Details',
      type: 'textarea',
      required: false,
      placeholder: 'Additional details about this material...',
      rows: 2,
      help: 'Optional detailed description'
    },
    {
      key: 'width_mm',
      label: 'Width (mm)',
      type: 'number',
      required: false,
      min: 0,
      placeholder: '1370',
      help: 'Width in millimeters (for rolls) or width of board'
    },
    {
      key: 'height_mm',
      label: 'Height (mm)',
      type: 'number',
      required: false,
      min: 0,
      placeholder: '1000',
      help: 'Height for boards or length for some materials'
    },
    {
      key: 'roll_length_m',
      label: 'Roll Length (meters)',
      type: 'number',
      required: false,
      min: 0,
      step: 0.1,
      placeholder: '50',
      help: 'Length of roll in meters (for roll materials only)'
    },
    {
      key: 'area_sqm',
      label: 'Area (m²)',
      type: 'number',
      required: false,
      min: 0,
      step: 0.01,
      placeholder: '1.37',
      help: 'Total area in square meters (for board materials)'
    },
    {
      key: 'unit_type',
      label: 'Unit Type',
      type: 'select',
      required: true,
      options: [
        { value: 'sqm', label: 'Per Square Meter (m²)' },
        { value: 'linear_m', label: 'Per Linear Meter (m)' },
        { value: 'item', label: 'Per Item' }
      ],
      defaultValue: 'sqm',
      help: 'How this material is priced and sold'
    },
    {
      key: 'price',
      label: 'Base Price (£)',
      type: 'number',
      required: false,
      step: 0.01,
      min: 0,
      placeholder: '25.00',
      help: 'Base price before markup'
    },
    {
      key: 'price_per_sqm',
      label: 'Price per m² (£)',
      type: 'number',
      required: false,
      step: 0.01,
      min: 0,
      placeholder: '18.25',
      help: 'Price per square meter (if unit type is sqm)'
    },
    {
      key: 'price_per_linear_m',
      label: 'Price per Linear Meter (£)',
      type: 'number',
      required: false,
      step: 0.01,
      min: 0,
      placeholder: '25.00',
      help: 'Price per linear meter (if unit type is linear_m)'
    },
    {
      key: 'markup_percent',
      label: 'Markup Percentage (%)',
      type: 'number',
      required: false,
      min: 0,
      placeholder: '100',
      help: 'Trade tier markup percentage applied to base price'
    },
    {
      key: 'retail_markup_percent',
      label: 'Retail Markup Percentage (%)',
      type: 'number',
      required: false,
      min: 0,
      placeholder: '150',
      help: 'Retail tier markup percentage applied to base price'
    },
    {
      key: 'category',
      label: 'Category',
      type: 'text',
      required: false,
      placeholder: 'e.g., Adhesive Vinyl, Rigid Board',
      help: 'Optional category for grouping materials'
    },
    {
      key: 'router_multiplier',
      label: 'Router Multiplier',
      type: 'number',
      required: false,
      step: 0.1,
      min: 0.1,
      defaultValue: '1.0',
      placeholder: '1.0',
      help: 'Multiplier for router cutting (board materials only, default: 1.0)'
    },
    {
      key: 'color_hex',
      label: 'Color Hex',
      type: 'text',
      required: false,
      placeholder: '#FF0000',
      help: 'Hex color code (e.g., #FF0000 for red)'
    }
  ]
};

// Transform functions for API communication
export const transformLfMaterialForApi = (formData) => {
  const transformed = {
    material_type: formData.material_type,
    name: formData.name,
    product_no: formData.product_no || '',
    detail: formData.detail || '',
    unit_type: formData.unit_type || 'sqm',
    category: formData.category || '',
    router_multiplier: parseFloat(formData.router_multiplier || 1.0),
    color_hex: formData.color_hex || ''
  };
  
  // Only include numeric fields if they have values
  const numericFields = [
    'width_mm', 'height_mm', 'roll_length_m', 'area_sqm', 
    'price', 'price_per_linear_m', 'price_per_sqm', 'markup_percent', 'retail_markup_percent'
  ];
  
  numericFields.forEach(field => {
    if (formData[field] && formData[field] !== '' && !isNaN(parseFloat(formData[field]))) {
      transformed[field] = parseFloat(formData[field]);
    }
  });
  
  return transformed;
};

export const transformLfMaterialFromApi = (apiData) => {
  return {
    id: apiData.id,
    material_type: apiData.material_type || '',
    name: apiData.name || '',
    product_no: apiData.product_no || '',
    detail: apiData.detail || '',
    width_mm: apiData.width_mm ? apiData.width_mm.toString() : '',
    height_mm: apiData.height_mm ? apiData.height_mm.toString() : '',
    roll_length_m: apiData.roll_length_m ? apiData.roll_length_m.toString() : '',
    area_sqm: apiData.area_sqm ? apiData.area_sqm.toString() : '',
    price: apiData.price ? apiData.price.toString() : '',
    price_per_linear_m: apiData.price_per_linear_m ? apiData.price_per_linear_m.toString() : '',
    price_per_sqm: apiData.price_per_sqm ? apiData.price_per_sqm.toString() : '',
    markup_percent: apiData.markup_percent ? apiData.markup_percent.toString() : '',
    retail_markup_percent: apiData.retail_markup_percent ? apiData.retail_markup_percent.toString() : '',
    unit_type: apiData.unit_type || 'sqm',
    category: apiData.category || '',
    router_multiplier: apiData.router_multiplier ? apiData.router_multiplier.toString() : '1.0',
    color_hex: apiData.color_hex || ''
  };
};

// Validation rules
export const validateLfMaterial = (data) => {
  const errors = {};
  
  if (!data.material_type) {
    errors.material_type = 'Material type is required';
  }
  
  if (!data.name || data.name.trim() === '') {
    errors.name = 'Material name is required';
  }
  
  if (!data.unit_type) {
    errors.unit_type = 'Unit type is required';
  }
  
  // Validate numeric fields
  const numericFields = [
    'width_mm', 'height_mm', 'roll_length_m', 'area_sqm', 
    'price', 'price_per_linear_m', 'price_per_sqm', 'markup_percent', 'retail_markup_percent', 'router_multiplier'
  ];
  
  // Validate color_hex format if provided
  if (data.color_hex && data.color_hex.trim() !== '') {
    const colorRegex = /^#[0-9A-Fa-f]{6}$/;
    if (!colorRegex.test(data.color_hex.trim())) {
      errors.color_hex = 'Color must be a valid 6-digit hex code (e.g., #FF0000)';
    }
  }
  
  numericFields.forEach(field => {
    if (data[field] && data[field] !== '') {
      const value = parseFloat(data[field]);
      if (isNaN(value) || value < 0) {
        errors[field] = `${field.replace(/_/g, ' ')} must be a non-negative number`;
      }
    }
  });
  
  // Validate that at least one pricing field is provided
  const hasPricing = data.price || data.price_per_sqm || data.price_per_linear_m;
  if (!hasPricing) {
    errors.price = 'At least one pricing field must be provided (base price, price per m², or price per linear meter)';
  }
  
  // Material-specific validation
  if (data.material_type === 'board') {
    if (!data.width_mm || !data.height_mm) {
      errors.width_mm = 'Board materials require width and height dimensions';
    }
  }
  
  if (data.material_type !== 'board' && data.router_multiplier && parseFloat(data.router_multiplier) !== 1.0) {
    errors.router_multiplier = 'Router multiplier is only applicable to board materials';
  }
  
  return Object.keys(errors).length > 0 ? errors : null;
};

// Helper function to get material type specific fields
export const getMaterialTypeFields = (materialType) => {
  const baseFields = ['material_type', 'name', 'product_no', 'detail', 'unit_type', 'price', 'markup_percent', 'retail_markup_percent', 'category'];
  
  switch (materialType) {
    case 'board':
      return [...baseFields, 'width_mm', 'height_mm', 'area_sqm', 'price_per_sqm', 'router_multiplier'];
    case 'vinyl':
    case 'banner':
    case 'poster':
    case 'laminate':
      return [...baseFields, 'width_mm', 'roll_length_m', 'price_per_linear_m', 'price_per_sqm'];
    default:
      return baseFields;
  }
};

// Material type templates for quick creation
export const MATERIAL_TEMPLATES = {
  vinyl: [
    {
      name: 'Premium Cast Vinyl White',
      unit_type: 'sqm',
      width_mm: 1370,
      price_per_sqm: 15.50,
      markup_percent: 100,
      category: 'Cast Vinyl'
    },
    {
      name: 'Calendered Vinyl Black',
      unit_type: 'sqm', 
      width_mm: 1270,
      price_per_sqm: 8.75,
      markup_percent: 100,
      category: 'Calendered Vinyl'
    }
  ],
  board: [
    {
      name: '3mm Foam Board White',
      unit_type: 'sqm',
      width_mm: 1000,
      height_mm: 1400,
      area_sqm: 1.4,
      price_per_sqm: 12.50,
      markup_percent: 100,
      category: 'Foam Board',
      router_multiplier: 1.0
    },
    {
      name: '5mm Correx Board',
      unit_type: 'sqm',
      width_mm: 1220,
      height_mm: 2440,
      area_sqm: 2.98,
      price_per_sqm: 8.25,
      markup_percent: 100,
      category: 'Corrugated Plastic',
      router_multiplier: 1.2
    }
  ],
  banner: [
    {
      name: '440gsm PVC Banner',
      unit_type: 'sqm',
      width_mm: 1370,
      price_per_sqm: 4.50,
      markup_percent: 100,
      category: 'PVC Banner'
    }
  ],
  poster: [
    {
      name: '200gsm Poster Paper',
      unit_type: 'sqm',
      width_mm: 1118,
      price_per_sqm: 2.25,
      markup_percent: 100,
      category: 'Paper'
    }
  ],
  laminate: [
    {
      name: 'Gloss Laminate',
      unit_type: 'sqm',
      width_mm: 1370,
      price_per_sqm: 3.75,
      markup_percent: 100,
      category: 'Protection Film'
    }
  ]
};

export default lfMaterialsTableConfig;